import React, { useMemo, useState } from 'react';
import DataGridToolbar from 'components/datagrid/DataGridToolbar';
import DeleteProductsDialog from './DeleteProductsDialog';
import SetCategoriesDialog from './SetCategoriesDialog';
import SetChannelsDialog from './SetChannelsDialog';
import SetDepositDialog from './SetDepositDialog';
import SetStoresDialog from './SetStoresDialog';
import { publishManyProducts, unpublishManyProducts } from './utils';
import { DataGridBulkAction } from 'components/datagrid/DataGridSelectedRowsActions';
import { useSelector } from 'react-redux';
import { useTranslation } from 'services/localization';

import * as ShopSelectors from 'selectors/ShopSelectors';
import { ProductsTableRow } from './rows';

const ProductsTableToolbar = () => {
	const { t } = useTranslation();
	const [activeModal, setActiveModal] = useState<{
		id: 'delete' | 'set-categories' | 'set-channels' | 'set-stores' | 'set-deposit';
		rowIds: string[];
	} | null>(null);

	const shopDepositPaymentMethods = useSelector(ShopSelectors.shopDepositPaymentMethods);
	const hasDepositPaymentMethodInUse = !!shopDepositPaymentMethods.length;

	const activeModalId = activeModal?.id;
	const activeModalRowIds = activeModal?.rowIds ?? [];

	const actions = useMemo(() => {
		const actions: DataGridBulkAction<ProductsTableRow>[] = [
			{
				key: 'publish',
				label: t('ProductsTableToolbar.publish', 'Make public'),
				handler: publishManyProducts,
				permissionsFields: ['canEdit', 'isNotSkidataProduct'],
			},
			{
				key: 'hide',
				label: t('ProductsTableToolbar.hide', 'Make hidden'),
				handler: unpublishManyProducts,
				permissionsFields: ['canEdit', 'isNotSkidataProduct'],
			},
			{
				key: 'set-categories',
				label: t('ProductsTableToolbar.setCategories', 'Set categories'),
				handler: (rowIds) => setActiveModal({ id: 'set-categories', rowIds }),
				permissionsFields: ['canEdit', 'isNotSkidataProduct'],
			},
			{
				key: 'set-channels',
				label: t('ProductsTableToolbar.setSalesChannels', 'Set sales channels'),
				handler: (rowIds) => setActiveModal({ id: 'set-channels', rowIds }),
				permissionsFields: ['canEdit', 'isNotKeycard'],
			},
			{
				key: 'set-stores',
				label: t('ProductsTableToolbar.setStores', 'Set stores'),
				handler: (rowIds) => setActiveModal({ id: 'set-stores', rowIds }),
				permissionsFields: ['canEdit', 'isNotKeycard'],
			},
		];

		if (hasDepositPaymentMethodInUse) {
			actions.push({
				key: 'set-deposit',
				label: t('ProductsTableToolbar.setDeposit', 'Set deposit'),
				handler: (rowIds) => setActiveModal({ id: 'set-deposit', rowIds }),
				permissionsFields: ['canEdit', 'isNotSkidataProduct'],
			});
		}

		return actions;
	}, [hasDepositPaymentMethodInUse, t]);

	const renderActiveDialog = () => {
		switch (activeModalId) {
			case 'delete':
				return (
					<DeleteProductsDialog
						productIds={activeModalRowIds}
						onClose={() => setActiveModal(null)}
					/>
				);
			case 'set-categories':
				return (
					<SetCategoriesDialog
						productIds={activeModalRowIds}
						onClose={() => setActiveModal(null)}
					/>
				);
			case 'set-channels':
				return (
					<SetChannelsDialog productIds={activeModalRowIds} onClose={() => setActiveModal(null)} />
				);
			case 'set-stores':
				return (
					<SetStoresDialog productIds={activeModalRowIds} onClose={() => setActiveModal(null)} />
				);
			case 'set-deposit':
				return (
					<SetDepositDialog productIds={activeModalRowIds} onClose={() => setActiveModal(null)} />
				);
			default:
				return null;
		}
	};

	return (
		<>
			<DataGridToolbar
				persistKey="ProductsTable"
				exportFileName="rentle-products"
				selectedRowsActions={{
					actions,
					deleteAction: {
						enabled: true,
						handler: (rowIds) => setActiveModal({ id: 'delete', rowIds }),
						permissionsFields: ['canEdit', 'isNotSkidataProduct'],
					},
					exportAction: {
						enabled: true,
					},
					getRowLabel: (row) => row.name,
				}}
			/>
			{renderActiveDialog()}
		</>
	);
};

export default ProductsTableToolbar;
